import { createContext, useContext, useEffect, useRef, useState } from 'react';

import { useColorScheme } from '@mui/material/styles';
import { MapPinIcon } from 'lucide-react';
import mapbox from 'mapbox-gl';
import type {
  Map,
  Marker,
  RequestTransformFunction,
  StyleSpecification,
} from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { createPortal } from 'react-dom';

import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { MAPBOX_TOKEN } from '@shared/config';
import { getDefaultLocationOnMap } from '@shared/LocaleContext';

export const MAPBOX_STYLE_STREETS = 'mapbox://styles/mapbox/streets-v12';
export const MAPBOX_STYLE_LIGHT = 'mapbox://styles/mapbox/light-v11';
export const MAPBOX_STYLE_DARK = 'mapbox://styles/mapbox/dark-v11';

// Create a context that will hold the map reference.
const MapContext = createContext<React.MutableRefObject<Map | null> | null>(
  null,
);

type MapRef = React.MutableRefObject<Map | null>;

interface MapboxProps {
  mapRef?: React.MutableRefObject<Map | null>;
  lat?: number;
  lng?: number;
  zoom?: number;
  defaultLat?: number;
  defaultLng?: number;
  defaultZoom?: number;
  style?: React.CSSProperties;
  mapStyle?: string | StyleSpecification;
  // Theme-aware styles. When provided, Mapbox resolves between them using its
  // own color scheme, so its color-scheme gate applies and a system-dark user
  // doesn't get the light basemap baked in for the session. Prefer these over
  // resolving the style yourself and passing a fixed `mapStyle`.
  mapStyleLight?: string | StyleSpecification;
  mapStyleDark?: string | StyleSpecification;
  // Off when the consumer adds its own AttributionControl — Mapbox pins the
  // default one bottom-right, which is where the Intercom launcher sits.
  attributionControl?: boolean;
  // Rewrites tile/style requests before Mapbox issues them (e.g. to attach the
  // CRM bearer token to our own tile routes). Read through a ref, so changing
  // it never recreates the map.
  transformRequest?: RequestTransformFunction;
  children?: React.ReactNode;
}

export const Mapbox = ({
  mapRef: propsMapRef,
  lat,
  lng,
  zoom,
  defaultLat,
  defaultLng,
  defaultZoom = 12,
  style,
  mapStyle,
  mapStyleLight,
  mapStyleDark,
  attributionControl = true,
  transformRequest,
  children,
}: MapboxProps) => {
  const internalMapRef = useRef<Map | null>(null);
  const mapRef = propsMapRef || internalMapRef;
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Resolved once at map creation (the init effect below bails when a map
  // already exists). Consumers that install custom layers only re-add them
  // on 'load', so a live setStyle() swap would silently wipe their layers.
  // For the default 'system' mode, systemMode is only known after mount, so we
  // gate creation until the scheme resolves — otherwise a system-dark user
  // would get the light basemap baked in for the whole session.
  const { mode, systemMode } = useColorScheme();
  const resolvedMode = mode === 'system' ? systemMode : mode;
  // Theme-aware styles (or the default basemap) must wait for the color scheme
  // to resolve. A fixed, theme-independent `mapStyle` is known immediately.
  const usesThemedStyle = mapStyleLight != null || mapStyleDark != null;
  const isColorSchemeResolved =
    (mapStyle != null && !usesThemedStyle) || resolvedMode != null;
  const resolvedMapStyle = usesThemedStyle
    ? resolvedMode === 'dark'
      ? mapStyleDark ?? MAPBOX_STYLE_DARK
      : mapStyleLight ?? MAPBOX_STYLE_LIGHT
    : mapStyle ||
      (resolvedMode === 'dark' ? MAPBOX_STYLE_DARK : MAPBOX_STYLE_STREETS);

  const { me } = useAppData();
  const myTeam = me?.default_team;
  const tenant = me?.tenant;
  const countryDefault = getDefaultLocationOnMap(tenant?.country_code ?? 'CH');
  // A default from the consumer (a restored viewport, an MLS search center, …)
  // is a deliberate choice and outranks the team/tenant home coordinates —
  // those only stand in when nobody said where to look. Home coordinates are
  // 0/0 when an address never geocoded, so treat zero as unset.
  const homeLng = myTeam?.lng || tenant?.lng || undefined;
  const homeLat = myTeam?.lat || tenant?.lat || undefined;
  // Legacy callers (PropertyDialog) pass 0/0 when there is no marker yet;
  // that is "no centre", not the Gulf of Guinea.
  const hasExplicitCentre =
    lng != null &&
    lat != null &&
    Number.isFinite(lng) &&
    Number.isFinite(lat) &&
    !(lng === 0 && lat === 0);
  const explicitLng = hasExplicitCentre ? lng : undefined;
  const explicitLat = hasExplicitCentre ? lat : undefined;
  const consumerLng =
    defaultLng != null && Number.isFinite(defaultLng) ? defaultLng : undefined;
  const consumerLat =
    defaultLat != null && Number.isFinite(defaultLat) ? defaultLat : undefined;
  const initialLng =
    explicitLng ?? consumerLng ?? homeLng ?? countryDefault.lng;
  const initialLat =
    explicitLat ?? consumerLat ?? homeLat ?? countryDefault.lat;

  const transformRequestRef = useRef(transformRequest);
  transformRequestRef.current = transformRequest;

  // Initialize map when component mounts
  useEffect(() => {
    if (!containerRef.current) {
      return;
    } // no map container
    if (mapRef.current) {
      return;
    } // initialize map only once
    if (!isColorSchemeResolved) {
      return;
    } // wait for the color scheme so we pick the right basemap

    mapRef.current = new mapbox.Map({
      container: containerRef.current,
      style: resolvedMapStyle,
      center: [initialLng, initialLat],
      zoom: zoom || defaultZoom,
      accessToken: MAPBOX_TOKEN,
      attributionControl,
      transformRequest: (url, resourceType) =>
        transformRequestRef.current?.(url, resourceType) ?? { url },
      projection: {
        name: 'globe',
      },
    });
  }, [
    attributionControl,
    mapRef,
    initialLat,
    initialLng,
    zoom,
    defaultZoom,
    resolvedMapStyle,
    isColorSchemeResolved,
  ]);

  // Mapbox only tracks window resizes. A sibling panel opening or collapsing
  // (app navigation, AI chat, a page's own sidebar) changes the container
  // without one and leaves a grey band where the canvas was not redrawn.
  // Follow the container itself: ResizeObserver fires on every frame of a
  // width transition, so the canvas keeps up with the animation too.
  useEffect(() => {
    const container = containerRef.current;
    if (container == null || typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(() => {
      mapRef.current?.resize();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [mapRef]);

  // Refresh map whenever center or zoom changes
  useEffect(() => {
    if (!mapRef.current) {
      return;
    } // wait for map to initialize
    if (lat === undefined || lng === undefined) {
      return;
    } // no center provided
    mapRef.current.flyTo({ center: [lng, lat], zoom, speed: 5 });
  }, [mapRef, lat, lng, zoom]);

  return (
    <MapContext.Provider value={mapRef}>
      <div
        ref={containerRef}
        style={{
          ...style,
          width: '100%',
          height: '100%',
          position: 'relative',
          minHeight: 'inherit',
          flex: 1,
        }}
        className="map-container"
      />
      {children}
    </MapContext.Provider>
  );
};

type Anchor =
  | 'center'
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

const useMarker = (
  markerRef: React.MutableRefObject<Marker | null>,
  lat: number,
  lng: number,
  anchor: Anchor = 'center',
  propsMapRef?: MapRef,
) => {
  const contextMapRef = useContext(MapContext);
  const mapRef = propsMapRef ?? contextMapRef;
  const map = mapRef?.current ?? null;
  const [container, setContainer] = useState<HTMLElement | null>(null);

  // initialize marker
  useEffect(() => {
    if (!map) {
      return;
    } // Do nothing if map is not initialized

    const element = document.createElement('div');
    const marker = new mapbox.Marker({ element, anchor })
      .setLngLat([lng, lat])
      .addTo(map);

    setContainer(element);
    markerRef.current = marker;

    // Cleanup function to remove marker from map on unmount
    return () => {
      marker.remove();
    };
  }, [map, markerRef, lat, lng, anchor]);

  return container;
};

interface StaticMarkerProps {
  mapRef?: MapRef;
  lat: number;
  lng: number;
  anchor?: Anchor;
  children?: React.ReactNode;
}

export const StaticMarker = ({
  mapRef,
  lat,
  lng,
  anchor,
  children,
}: StaticMarkerProps) => {
  const markerRef = useRef<Marker | null>(null);
  const container = useMarker(markerRef, lat, lng, anchor, mapRef);
  useEffect(() => {
    if (container) {
      container.style.cursor = 'arrow';
    }
  }, [container]);
  return container && createPortal(children, container);
};

type LngLatLike = {
  lat: number;
  lng: number;
};

interface DraggableMarkerProps {
  mapRef?: MapRef;
  lat: number;
  lng: number;
  anchor?: Anchor;
  onDragend: (latLng: LngLatLike) => void;
  children: ({ dragging }: { dragging: boolean }) => React.ReactNode;
}

export const DraggableMarker = ({
  mapRef,
  lat,
  lng,
  anchor,
  onDragend,
  children,
}: DraggableMarkerProps) => {
  const markerRef = useRef<Marker | null>(null);
  const [dragging, setDragging] = useState(false);
  const container = useMarker(markerRef, lat, lng, anchor, mapRef);

  useEffect(() => {
    if (container && markerRef.current) {
      container.style.cursor = 'move';
      markerRef.current.setDraggable(true);
    }
  }, [container]);

  useEffect(() => {
    const marker = markerRef.current;
    if (marker) {
      const handleDragstart = () => setDragging(true);
      const handleDragend = (event: any) => {
        // Type for mapbox dragend event
        const lngLat = event.target.getLngLat();
        onDragend({ lat: lngLat.lat, lng: lngLat.lng });
        setDragging(false);
      };
      marker.on('dragstart', handleDragstart);
      marker.on('dragend', handleDragend);
      return () => {
        marker.off('dragstart', handleDragstart);
        marker.off('dragend', handleDragend);
      };
    }
  }, [onDragend]);

  return container ? createPortal(children({ dragging }), container) : null;
};

// LocationMarkerProps type definition
interface LocationMarkerProps {
  mapRef?: MapRef;
  lat: number;
  lng: number;
  onDragend?: (latLng: LngLatLike) => void;
}

export const LocationMarker = ({
  mapRef,
  lat,
  lng,
  onDragend,
}: LocationMarkerProps) => {
  // Define your style object here
  const style = {
    filter: [
      'drop-shadow(1px 1px 1px rgba(255, 255, 255, 1))',
      'drop-shadow(1px -1px 1px rgba(255, 255, 255, 1))',
      'drop-shadow(-1px 1px 1px rgba(255, 255, 255, 1))',
      'drop-shadow(-1px -1px 1px rgba(255, 255, 255, 1))',
    ].join(' '),
    width: 40,
    height: 40,
    color: '#2196f3',
  };

  if (onDragend) {
    return (
      // Your JSX for draggable marker, replace DraggableMarker with your actual component
      <DraggableMarker
        mapRef={mapRef}
        lat={lat}
        lng={lng}
        anchor="bottom"
        onDragend={onDragend}
      >
        {({ dragging }) => (
          <MapPinIcon
            css={[
              style,
              {
                transition: 'all 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                willChange: 'width, height',
                ':hover': { color: '#64b5f6', width: 44, height: 44 },
                '&, &:hover': dragging
                  ? { color: '#64b5f6', width: 52, height: 52 }
                  : {},
              },
            ]}
          />
        )}
      </DraggableMarker>
    );
  } else {
    return (
      // Your JSX for static marker, replace LocationOn with your actual component
      <StaticMarker mapRef={mapRef} lat={lat} lng={lng} anchor="bottom">
        <MapPinIcon style={style} />
      </StaticMarker>
    );
  }
};
